import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Check, Home, Info, Minus, Plus, Tent } from "lucide-react";
import { useI18n } from "../context/I18nContext";
import api, { formatApiError } from "../lib/api";
import Seo from "../components/Seo";
import { Overline, Reveal, EASE } from "../components/Reveal";

const todayISO = () => new Date().toISOString().slice(0, 10);

function Stepper({ value, onChange, invalid }) {
  const clamp = (v) => Math.min(40, Math.max(1, v));
  return (
    <div
      data-testid="booking-field-personas"
      className={`flex items-center justify-between border px-2 py-1.5 ${
        invalid ? "border-terra-dark" : "border-charcoal/20"
      }`}
    >
      <button
        type="button"
        data-testid="booking-personas-minus"
        onClick={() => onChange(clamp(value - 1))}
        className="p-2 text-charcoal/60 transition-colors hover:text-terra"
        aria-label="-"
      >
        <Minus size={16} />
      </button>
      <span className={`font-serif text-xl ${invalid ? "text-terra-dark" : ""}`}>{value}</span>
      <button
        type="button"
        data-testid="booking-personas-plus"
        onClick={() => onChange(clamp(value + 1))}
        className="p-2 text-charcoal/60 transition-colors hover:text-terra"
        aria-label="+"
      >
        <Plus size={16} />
      </button>
    </div>
  );
}

export default function Reservas() {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [errors, setErrors] = useState({});

  const langToIdioma = { es: "Español", en: "English", fr: "Français" };
  const [form, setForm] = useState({
    nombre: "",
    apellidos: "",
    email: "",
    telefono: "",
    fecha: "",
    personas: 4,
    tipo: searchParams.get("tipo") === "domicilio" ? "domicilio" : "edouard",
    direccion: "",
    idioma: langToIdioma[lang] || "Español",
    alergias: "",
    mensaje: "",
    acepta: false,
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const personasInvalid = form.personas < 4;
  const f = t.reservas.form;

  const validate = () => {
    const errs = {};
    if (!form.nombre.trim()) errs.nombre = f.required;
    if (!form.email.trim()) errs.email = f.required;
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = f.invalidEmail;
    if (!form.telefono.trim()) errs.telefono = f.required;
    if (!form.fecha) errs.fecha = f.fechaRequired;
    else if (form.fecha < todayISO()) errs.fecha = f.fechaRequired;
    if (personasInvalid) errs.personas = f.personasMin;
    if (form.tipo === "domicilio" && !form.direccion.trim()) errs.direccion = f.direccionRequired;
    if (!form.acepta) errs.acepta = f.aceptoRequired;
    return errs;
  };

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setSending(true);
    try {
      await api.post("/bookings", {
        nombre: form.nombre,
        apellidos: form.apellidos,
        email: form.email,
        telefono: form.telefono,
        fecha_deseada: form.fecha,
        personas: Number(form.personas),
        tipo_experiencia: form.tipo,
        direccion: form.direccion,
        idioma: form.idioma,
        alergias: form.alergias,
        mensaje: form.mensaje,
        acepta_confirmacion: form.acepta,
      });
      setSent(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiError(err, f.error));
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    const s = t.reservas.success;
    return (
      <div className="flex min-h-screen items-center justify-center bg-cream px-5 py-32 text-charcoal">
        <Seo title={`${s.title} — Wild Spoon`} description={t.seo.reservas.description} />
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: EASE }}
          className="max-w-xl border border-charcoal/12 bg-white/70 p-10 text-center md:p-14"
          data-testid="booking-success"
        >
          <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-olive/15">
            <Check size={30} className="text-olive" />
          </span>
          <h1 className="mt-7 font-serif text-3xl md:text-4xl">{s.title}</h1>
          <div className="mt-6 space-y-3 leading-relaxed text-charcoal/70">
            <p>{s.p1}</p>
            <p>{s.p2}</p>
            <p className="font-medium text-charcoal">{s.p3}</p>
          </div>
          <button
            data-testid="booking-success-home"
            onClick={() => navigate("/")}
            className="mt-9 rounded-full bg-charcoal px-8 py-3.5 text-sm font-medium text-cream transition-colors hover:bg-terra"
          >
            {s.cta}
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream py-28 text-charcoal md:py-36">
      <Seo title={t.seo.reservas.title} description={t.seo.reservas.description} />
      <div className="mx-auto grid max-w-7xl gap-14 px-5 md:px-10 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-28">
            <Reveal>
              <Overline>{t.reservas.overline}</Overline>
            </Reveal>
            <Reveal delay={0.08}>
              <h1 className="mt-6 font-serif text-4xl leading-tight md:text-5xl">{t.reservas.title}</h1>
            </Reveal>
            <Reveal delay={0.14}>
              <p className="mt-6 max-w-md leading-relaxed text-charcoal/70">{t.reservas.lead}</p>
            </Reveal>
            <Reveal delay={0.2}>
              <div
                data-testid="booking-notice"
                className="mt-8 flex max-w-md gap-3 border-l-2 border-terra bg-bone p-5 text-sm leading-relaxed text-charcoal/80"
              >
                <Info size={18} className="mt-0.5 shrink-0 text-terra" />
                {t.reservas.notice}
              </div>
            </Reveal>
            <Reveal delay={0.26}>
              <p className="mt-10 text-[11px] font-semibold uppercase tracking-[0.25em] text-stonekw">
                {t.reservas.stepsTitle}
              </p>
              <ol className="mt-5 max-w-md space-y-6">
                {t.reservas.steps.map((s, i) => (
                  <li key={i} className="flex gap-5">
                    <span className="font-corm text-3xl italic leading-none text-terra/80">0{i + 1}</span>
                    <div>
                      <p className="font-serif text-lg">{s.t}</p>
                      <p className="mt-1 text-sm leading-relaxed text-charcoal/60">{s.d}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </Reveal>
          </div>
        </div>

        <div className="lg:col-span-6 lg:col-start-7">
          <Reveal delay={0.15} y={40}>
            <form
              data-testid="booking-request-form"
              onSubmit={submit}
              noValidate
              className="border border-charcoal/12 bg-white/70 p-7 md:p-10"
            >
              <div className="grid gap-6 sm:grid-cols-2">
                <div>
                  <label className="field-label" htmlFor="b-nombre">{f.nombre} *</label>
                  <input
                    id="b-nombre"
                    data-testid="booking-field-nombre"
                    className="field"
                    placeholder={f.nombrePh}
                    value={form.nombre}
                    onChange={(e) => set("nombre", e.target.value)}
                  />
                  {errors.nombre && <p className="mt-1.5 text-xs text-terra-dark">{errors.nombre}</p>}
                </div>
                <div>
                  <label className="field-label" htmlFor="b-apellidos">{f.apellidos}</label>
                  <input
                    id="b-apellidos"
                    data-testid="booking-field-apellidos"
                    className="field"
                    placeholder={f.apellidosPh}
                    value={form.apellidos}
                    onChange={(e) => set("apellidos", e.target.value)}
                  />
                </div>
                <div>
                  <label className="field-label" htmlFor="b-email">{f.email} *</label>
                  <input
                    id="b-email"
                    data-testid="booking-field-email"
                    type="email"
                    className="field"
                    placeholder={f.emailPh}
                    value={form.email}
                    onChange={(e) => set("email", e.target.value)}
                  />
                  {errors.email && <p className="mt-1.5 text-xs text-terra-dark">{errors.email}</p>}
                </div>
                <div>
                  <label className="field-label" htmlFor="b-telefono">{f.telefono} *</label>
                  <input
                    id="b-telefono"
                    data-testid="booking-field-telefono"
                    type="tel"
                    className="field"
                    placeholder={f.telefonoPh}
                    value={form.telefono}
                    onChange={(e) => set("telefono", e.target.value)}
                  />
                  {errors.telefono && <p className="mt-1.5 text-xs text-terra-dark">{errors.telefono}</p>}
                </div>
                <div>
                  <label className="field-label" htmlFor="b-fecha">{f.fecha} *</label>
                  <input
                    id="b-fecha"
                    data-testid="booking-field-fecha"
                    type="date"
                    min={todayISO()}
                    className="field"
                    value={form.fecha}
                    onChange={(e) => set("fecha", e.target.value)}
                  />
                  {errors.fecha && <p className="mt-1.5 text-xs text-terra-dark">{errors.fecha}</p>}
                </div>
                <div>
                  <label className="field-label">{f.personas} *</label>
                  <Stepper value={form.personas} onChange={(v) => set("personas", v)} invalid={personasInvalid} />
                  {personasInvalid ? (
                    <p data-testid="booking-personas-error" className="mt-1.5 text-xs text-terra-dark">
                      {f.personasMin}
                    </p>
                  ) : null}
                </div>
              </div>

              <div className="mt-8">
                <label className="field-label">{f.tipo} *</label>
                <div className="grid gap-4 sm:grid-cols-2">
                  {[
                    { v: "edouard", label: f.tipoEdouard, hint: f.tipoEdouardHint, icon: Home, testid: "booking-tipo-edouard" },
                    { v: "domicilio", label: f.tipoDomicilio, hint: f.tipoDomicilioHint, icon: Tent, testid: "booking-tipo-domicilio" },
                  ].map(({ v, label, hint, icon: Icon, testid }) => (
                    <button
                      key={v}
                      type="button"
                      data-testid={testid}
                      onClick={() => set("tipo", v)}
                      className={`flex items-start gap-3 border p-4 text-left transition-all duration-300 ${
                        form.tipo === v
                          ? "border-terra bg-terra/5"
                          : "border-charcoal/20 hover:border-charcoal/40"
                      }`}
                    >
                      <Icon size={18} className={`mt-0.5 shrink-0 ${form.tipo === v ? "text-terra" : "text-charcoal/50"}`} />
                      <span>
                        <span className="block text-sm font-medium">{label}</span>
                        <span className="mt-0.5 block text-xs text-charcoal/55">{hint}</span>
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              <motion.div
                initial={false}
                animate={{ height: form.tipo === "domicilio" ? "auto" : 0, opacity: form.tipo === "domicilio" ? 1 : 0 }}
                transition={{ duration: 0.4, ease: EASE }}
                className="overflow-hidden"
              >
                {form.tipo === "domicilio" && (
                  <div className="mt-6">
                    <label className="field-label" htmlFor="b-direccion">{f.direccion} *</label>
                    <input
                      id="b-direccion"
                      data-testid="booking-field-direccion"
                      className="field"
                      placeholder={f.direccionPh}
                      value={form.direccion}
                      onChange={(e) => set("direccion", e.target.value)}
                    />
                    {errors.direccion && <p className="mt-1.5 text-xs text-terra-dark">{errors.direccion}</p>}
                  </div>
                )}
              </motion.div>

              <div className="mt-6 grid gap-6 sm:grid-cols-2">
                <div>
                  <label className="field-label" htmlFor="b-idioma">{f.idioma}</label>
                  <select
                    id="b-idioma"
                    data-testid="booking-field-idioma"
                    className="field"
                    value={form.idioma}
                    onChange={(e) => set("idioma", e.target.value)}
                  >
                    {f.idiomas.map((i) => (
                      <option key={i} value={i}>
                        {i}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="mt-6">
                <label className="field-label" htmlFor="b-alergias">{f.alergias}</label>
                <textarea
                  id="b-alergias"
                  data-testid="booking-field-alergias"
                  className="field min-h-20 resize-y"
                  placeholder={f.alergiasPh}
                  value={form.alergias}
                  onChange={(e) => set("alergias", e.target.value)}
                />
              </div>

              <div className="mt-6">
                <label className="field-label" htmlFor="b-mensaje">{f.mensaje}</label>
                <textarea
                  id="b-mensaje"
                  data-testid="booking-field-mensaje"
                  className="field min-h-20 resize-y"
                  placeholder={f.mensajePh}
                  value={form.mensaje}
                  onChange={(e) => set("mensaje", e.target.value)}
                />
              </div>

              <div className="mt-8">
                <button
                  type="button"
                  data-testid="booking-acepto-checkbox"
                  onClick={() => set("acepta", !form.acepta)}
                  className="flex items-start gap-3 text-left"
                >
                  <span
                    className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center border transition-colors ${
                      form.acepta ? "border-terra bg-terra text-cream" : "border-charcoal/30 bg-transparent"
                    }`}
                  >
                    {form.acepta && <Check size={13} />}
                  </span>
                  <span className="text-xs leading-relaxed text-charcoal/70">{f.acepto} *</span>
                </button>
                {errors.acepta && <p className="mt-1.5 text-xs text-terra-dark">{errors.acepta}</p>}
              </div>

              <button
                data-testid="booking-submit-button"
                type="submit"
                disabled={sending || personasInvalid}
                className="mt-9 w-full rounded-full bg-terra px-8 py-4 text-sm font-medium text-cream transition-all duration-300 hover:bg-terra-dark disabled:cursor-not-allowed disabled:opacity-50"
              >
                {sending ? f.submitting : f.submit}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </div>
  );
}
